import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import config from '../../config';
import logoCompact from '../../img/logo-2.svg';
import ContractsModal from '../ContractsModal';
import {
  Container,
  Content,
  Item,
  ItemsArea,
  NameLetterIcon,
  NotificationsItemsArea,
  UserContent,
} from './styles';
import * as courseSelectors from '../../redux/selectors/course';
import { useHistory } from 'react-router-dom';
import { actions as studentActions } from '../../redux/modules/student';
import { actions as authActions } from '../../redux/modules/auth';
import { RenderNotificationTagNumber } from 'components/NotificationTagNumber';
import sidebarLinks from '../../shared/SideBar/links';

interface MenuItem {
  name: string;
  title: string;
  link?: boolean;
  openURL?: string;
  show?: boolean;
  showMobile?: boolean;
  border?: boolean;
}

interface RootState {
  student: {
    user: {
      foto?: string;
      name?: string;
      id_profile?: string | number;
    };
  };
  auth: {
    user?: {
      id_profile?: string | number;
    };
  };
}

function Top({ logout, cursos }: any) {
  const dispatch = useDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const itemsAreaRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const {
    student: { user },
  } = useSelector((state: RootState) => state);

  const { foto, name } = user || {};

  useEffect(() => {
    const getUsarData = JSON.parse(localStorage.getItem('persist:root') || '{}');
    if (getUsarData) {
      const { user } = JSON.parse(getUsarData.auth || '{}');
      if (user?.id_profile) dispatch(studentActions.fetchStudentsById(user?.id_profile));
    }
  }, [dispatch]);

  // Handle click outside to close ItemsArea
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileOpen &&
        itemsAreaRef.current &&
        moreButtonRef.current &&
        !itemsAreaRef.current.contains(event.target as Node) &&
        !moreButtonRef.current.contains(event.target as Node)
      ) {
        setMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileOpen]);

  const history = useHistory();

  const handleGoToProfile = useCallback(() => {
    history.push('/user');
  }, [history]);

  const handleOpenMobile = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenAppQuiz = () => {
    window.open(config.REACT_APP_APP_QUIZ_URL, '_blank', 'noopener,noreferrer');
  };

  const onClick = useCallback(
    (item: MenuItem | string) => {
      setMobileOpen(false);

      if (typeof item === 'string') {
        history.push(`/${item}`);
        return;
      }

      if (item?.openURL) {
        window.open(item.openURL, '_blank', 'noopener,noreferrer');
        return;
      }

      switch (item.name) {
        case 'logout':
          logout();
          history.push('/');
          break;
        case 'appQuiz':
          handleOpenAppQuiz();
          break;
        case 'contracts':
          setShowContractsModal(true);
          break;
        default:
          history.push(`/${item.name}`);
          break;
      }
    },
    [history, logout]
  );

  return useMemo(
    () => (
      <>
        <Container>
          <Content>
            <img src={logoCompact} alt="ART" className="logo" />
          </Content>
          <UserContent onClick={handleGoToProfile}>
            {foto ? (
              <div
                style={{
                  backgroundImage: `url(${config.s3Url + foto})`,
                }}
              />
            ) : (
              <NameLetterIcon>{name?.slice(0, 1)}</NameLetterIcon>
            )}
          </UserContent>
          <NotificationsItemsArea>
            <Item key="notifications" id="notifications" onClick={() => onClick('notifications')}>
              <span>Notificações</span>
              <RenderNotificationTagNumber name="notifications" />
            </Item>
            <Item
              key="more"
              id="more"
              onClick={handleOpenMobile}
              ref={moreButtonRef}
              aria-expanded={mobileOpen}
              aria-haspopup="true"
              aria-label="Menu de navegação adicional"
            >
              <span>Mais itens</span>
            </Item>
          </NotificationsItemsArea>
          {mobileOpen && (
            <ItemsArea ref={itemsAreaRef} role="menu" aria-label="Itens de navegação">
              {sidebarLinks(cursos)
                .filter((item: MenuItem) => {
                  let envHiddenMenu = false;
                  try {
                    envHiddenMenu = config.hiddenMenu.includes(item.name);
                  } catch {
                    // Any exception i gonna ignore, cause probably the environment variable was passed incorrect
                  }

                  return (
                    (typeof item.showMobile === 'undefined' || item?.showMobile) &&
                    (typeof item.show === 'undefined' || item?.show) &&
                    !envHiddenMenu
                  );
                })
                .map((item: MenuItem) => (
                  <Item
                    key={item.name}
                    id={item.name}
                    border={!!item.border}
                    onClick={() => onClick(item)}
                    role="menuitem"
                  >
                    <span>{item.title}</span>
                    <RenderNotificationTagNumber name={item.name} />
                  </Item>
                ))}
            </ItemsArea>
          )}
        </Container>
        {showContractsModal && <ContractsModal setVisible={setShowContractsModal} />}
      </>
    ),
    // eslint-disable-next-line
    [foto, name, handleGoToProfile, mobileOpen, cursos, onClick, showContractsModal]
  );
}

const mapStateToProps = (state: any) => ({
  cursos: courseSelectors.getEnrollmentsList(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(authActions.logout()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Top);
